import { useCallback, useEffect, useState } from 'react'

import { useTranslation } from 'react-i18n-lite'

import {
  DocumentListCard,
  Field,
  FilteractionObject
} from '@yes.technology/react-toolkit'

import useObjectClassFilteractions from 'filteraction/shared/hooks/useObjectClassFilteractions'

import { FieldSettings } from 'shared/types'

import SectionDivider from 'site/Renderer/components/SectionDivider'

import { useSiteState } from 'siteState/shared'

import { useSituations } from 'situation/shared'

import { FilterFieldsDisplayOptions } from '../../FilterFields'
import { FilterActionSetup, FilterButton } from '../../FilterFields.styles'

import { formatFields } from '../../shared'

import { FieldValues, FilterAreaLayout, GroupedFieldValues } from '../../types'

import { isEqual } from 'lodash'
import { AllowedLocales } from 'shared/utils/date/formatDate'
import { ConcatFields } from 'shared/utils/object/concatenateFields'
import Fields from '../Fields/Fields'
import FilteractionSelection from '../FilteractionSelection/FilteractionSelection'
import getOnSelectFilteraction from '../FilteractionSelection/getOnSelectFilteraction'
import getFilterStateFromFilteraction from '../FilteractionSelection/getQueryDataFromFilteraction'
import FilterComposition from '../FilterComposition/FilterComposition'
import GroupControls from '../GroupControls/GroupControls'
import removeGroupFromComposition from './removeGroupFromComposition'
import { EmbeddedWrapper } from './Section.styles'

type FieldsProps = {
  filterAreaLayout?: FilterAreaLayout
  sections: Field[][]
  groupedFieldValues: GroupedFieldValues
  initialFieldValues: FieldValues
  composition: string
  enableComposition?: boolean
  title?: string
  handleChange: (value: Partial<GroupedFieldValues>) => void
  handleClear: () => void
  handleComposition: (composition: string) => void
  uuidCurrentSitemodelObjectclass?: string
  navigationSliderBackgroundColor?: string
  navigationSliderColor?: string
  defaultExecutedFilteraction?: string
  executeFilter: (fieldValues: GroupedFieldValues, composition: string) => void
  elementUuid: string
  fieldSettings?: FieldSettings
  concatFields?: ConcatFields
  filteractionOptions?: FilteractionObject[]
  dependsOnFilteractionOptions?: boolean
  embedded?: boolean
  selectAndLinkTargetPath?: string
}

const Section = ({
  filterAreaLayout,
  sections,
  initialFieldValues,
  groupedFieldValues,
  title,
  handleChange,
  handleClear,
  composition,
  enableComposition = false,
  handleComposition,
  uuidCurrentSitemodelObjectclass = '',
  navigationSliderBackgroundColor,
  navigationSliderColor,
  defaultExecutedFilteraction,
  executeFilter,
  elementUuid,
  fieldSettings,
  concatFields,
  filteractionOptions,
  selectAndLinkTargetPath,
  dependsOnFilteractionOptions,
  embedded = false
}: FieldsProps) => {
  const { t, language } = useTranslation()

  const locale = language as AllowedLocales

  const [sectionPosition, setSectionPosition] = useState<number>(1)

  const [changedKeysData, setChangedKeysData] = useState<GroupedFieldValues>()

  const sectionsLength = sections.length

  const currentFields = formatFields(sections[sectionPosition - 1])

  const [groupPosition, setGroupPosition] = useState(1)

  const groupsLength = Object.keys(groupedFieldValues).length

  if (groupsLength < groupPosition) {
    setGroupPosition(1)
  }

  const [filteractionValue, setFilteractionValue] = useState('')

  const filteractions = useObjectClassFilteractions({
    objectclassUuid: uuidCurrentSitemodelObjectclass,
    filteractionOptions,
    dependsOnFilteractionOptions
  })

  const situations = useSituations()

  const onSelectFilteraction = getOnSelectFilteraction({
    initialFieldValues,
    sections,
    situations,
    handleChange,
    handleClear,
    handleComposition,
    setGroupPosition,
    setChangedKeysData,
    locale
  })

  const fieldValues = groupedFieldValues[groupPosition]

  const handleChangeFieldValues = (value: FieldValues) => {
    handleChange({ [groupPosition]: value })
  }

  const handleClearCurrentGroup = () =>
    handleChangeFieldValues(initialFieldValues)

  const handleClearAll = () => {
    handleClear?.()

    setFilteractionValue('')
  }

  useEffect(() => {
    if (
      defaultExecutedFilteraction &&
      filteractions.length > 0 &&
      situations.length > 0
    ) {
      const filteraction = filteractions.find(
        (filteraction) => filteraction.uuid === defaultExecutedFilteraction
      )

      if (filteraction) {
        const { queryData, composition } = getFilterStateFromFilteraction({
          initialFieldValues,
          sections,
          situations,
          filteraction,
          locale
        })

        executeFilter(queryData, composition)

        setFilteractionValue(filteraction.des)

        handleChange(queryData)

        handleComposition(composition)
      }
    }
  }, [
    defaultExecutedFilteraction,
    filteractions,
    situations,
    executeFilter,
    sections,
    initialFieldValues,
    handleComposition,
    handleChange,
    locale
  ])

  const hideFiltersAfterFilteraction =
    !!filterAreaLayout?.filteractions?.hide_filters_after_filteraction

  const shouldHideFilters = hideFiltersAfterFilteraction && !!filteractionValue

  const sectionLayout = filterAreaLayout?.filter_sections?.[sectionPosition - 1]

  const [displayOptions] = useSiteState<FilterFieldsDisplayOptions>(
    'filter-display-options'
  )

  const displayOptionsElement = displayOptions?.[elementUuid]

  const interdependentFilteringConfig =
    filterAreaLayout?.interdependent_filtering

  const handleCreateGroup = useCallback(() => {
    handleChange({
      [groupsLength + 1]: initialFieldValues
    })
    setGroupPosition((currentGroup) => currentGroup + 1)
  }, [groupsLength, handleChange, initialFieldValues])

  const Divider = embedded ? EmbeddedWrapper : SectionDivider

  const isEmpty =
    groupsLength === 1 &&
    filteractionValue === '' &&
    isEqual(fieldValues, initialFieldValues)

  const handleRemoveCurrentGroup = () => {
    handleComposition(removeGroupFromComposition(groupPosition, composition))

    if (groupsLength === groupPosition) {
      setGroupPosition((currentGroup) => currentGroup - 1)
    }

    handleChange({
      [groupPosition]: undefined
    })
  }

  return (
    <Divider
      title={`${title} | ${t('filter-fields.filters')}`}
      backgroundColor='#004099'
      isVisible={!displayOptionsElement?.hideMainCollapsibleContainer}
    >
      <DocumentListCard style={{ padding: 8 }}>
        <FilterActionSetup>
          <FilterButton
            type='submit'
            className='flex-grow-1 flex-md-grow-0 fw-semibold'
            heightClass='regular'
          >
            {t('filter-fields.filter')}
          </FilterButton>
          <FilterButton
            variant='secondary'
            onClick={handleClearAll}
            className='flex-grow-1 flex-md-grow-0 fw-semibold'
            heightClass='regular'
            disabled={isEmpty}
          >
            {t('filter-fields.clean')}
          </FilterButton>
          {!displayOptionsElement?.hideFilteraction && (
            <FilteractionSelection
              value={filteractionValue}
              setValue={setFilteractionValue}
              {...{ filteractions, onSelectFilteraction }}
            />
          )}
          {enableComposition && (
            <FilterComposition
              {...{
                composition,
                handleComposition,
                handleCreateGroup,
                groupsLength
              }}
            />
          )}
        </FilterActionSetup>
        <GroupControls
          onRemoveCurrentGroup={handleRemoveCurrentGroup}
          {...{
            sectionPosition,
            sectionsLength,
            groupPosition,
            groupsLength,
            setGroupPosition,
            setSectionPosition,
            handleClearCurrentGroup,
            navigationSliderBackgroundColor,
            navigationSliderColor,
            shouldHideFilters,
            elementUuid,
            enableComposition
          }}
        >
          <div className='px-3'>
            {currentFields?.length > 0 && (
              <Fields
                sectionLayout={sectionLayout}
                fields={currentFields}
                handleChange={handleChangeFieldValues}
                {...{
                  fieldValues,
                  uuidCurrentSitemodelObjectclass,
                  groupedFieldValues,
                  interdependentFilteringConfig,
                  fieldSettings,
                  concatFields,
                  selectAndLinkTargetPath,
                  groupPosition,
                  changedKeysData
                }}
              />
            )}
          </div>
        </GroupControls>
      </DocumentListCard>
    </Divider>
  )
}

export default Section
